import { createSlice, createAsyncThunk, PayloadAction, createEntityAdapter, nanoid } from "@reduxjs/toolkit";
import produce from "immer";
import { requestsService } from "../../services/requests-service";
import { BUSY_TYPES } from "../../utils/busy";
import { REQUESTS_TYPE, requestStages } from "../../utils/brm-utils";
import { Column, FetchTableDataParams, TableFilterData, TableSortData } from "../../types/common/table/table-types";
import {
    GetRequestResponse,
    RequestState,
    GetRequestStatusesResponse,
    RequestDetailed,
    RequestModelingRole,
    RequestRole,
    CreateRequestForm,
    ChangeRequest,
    RequestComment,
    PreparationForTestingRequestFormState,
    PostRequestModelingRole,
} from "@/types/brm";
import { ResponseError } from "../../types/common/responses/responses-types";
import { reportsModelingService } from "../../services/reports-modeling-service";
import { RootState } from "../../store";
import { getSelectedRoleReducers } from "../reports-modeling/selectedRoleReducers";
import {
    getDefaultPermissionsValuesForTransactions,
    getDefaultPermissionsValuesForTransactionsMap,
    getDeletedTcodesFromMenu,
    getTcodesMapCountFromMenu,
    getTransactionFromRoleMenu,
    getTransactionsByIdsForSystemsGroup,
} from "../../utils/role-menu";
import { findFieldInTree, findItemInTree, TreeWalker } from "../../utils/tree-table";
import {
    getDeletedDefaultValuesForTcodes,
    recalcRoleTreeTrafficLightStatus,
    REPORT_MODELING_CONFLICT_LEVELS,
} from "../../utils/reports-modeling-utils";
import { REPORT_LEVELS, REPORT_TYPES } from "../../utils/sodReports";
import LockManager from "../../utils/lockManager";
import { ReportColumn } from "../../types/report/report-types";

const modelingRoles = createEntityAdapter<RequestModelingRole>();
const lockManager = new LockManager("change-request");

const initialState: RequestState["administration"]["requests"] = {
    requests: [],
    columns: [],
    total: 0,
    selectedIds: [],
    searchString: "",
    pageNumber: 1,
    limitNumber: 10,
    sortData: [],
    filterData: [],
    busy: false,
    busyType: null,
    error: null,
    warning: null,
    validationErrors: null,
    requestsType: REQUESTS_TYPE.ALL,
    requestStatuses: null,
    currentRequest: null,
    requestStages: [],
    rolesForView: {
        roles: [],
        columns: [],
        total: 0,
        selectedRoles: [],
        searchString: "",
        pageNumber: 1,
        limitNumber: 10,
        sortData: [],
        filterData: [],
    },
    reportState: {
        rows: [],
        columns: [],
        total: 0,
        pageNumber: 1,
        limitNumber: 10,
        filterData: [],
        sortData: [],
        searchString: "",
        selectedIds: [],
        reportLevel: REPORT_LEVELS.OPERATIONS,
        reportType: REPORT_TYPES.FINAL,
        conflictLevel: REPORT_MODELING_CONFLICT_LEVELS.ROLE,
    },
    modeling: {
        selectedRoleId: "",
        roles: modelingRoles.getInitialState(),
    },
};

type stateType = typeof initialState;

function addGroupRoleChildren(newRoleChild, newChildRoleId, children) {
    const newChildren = [...children];
    const alreadyInChildren = newChildren?.some((child) => child.role === newRoleChild.role);

    if (newChildren && !alreadyInChildren) {
        newChildren.push({
            ...newRoleChild,
            id: newChildRoleId,
            isChild: true,
            changed: newRoleChild.changed || false,
        });
    }
    return newChildren;
}

const createRoleForModeling = (role: RequestRole, groupId: string, initialized = false): RequestModelingRole => {
    // let profile = null;

    // if (role.isNew) {
    //     profile = "profile" in role ? role.profile || role.role : role.role
    // }

    return {
        id: role.id,
        // id: nanoid(),
        role: role.role,
        systemId: role?.systemId ?? "", 
        groupId: groupId,
        description: role.description,
        initialized: initialized ? false : role.isComplex,
        isComplex: role.isComplex,
        menu: reportsModelingService.parseRoleMenu([]),
        children: role.isComplex && role.children ? role.children : [],
        tree: [],
        orgLevels: {},
        delta: [],
        orgLevelsDelta: [],
        profile: "profile" in role ? role.profile || role.role : role.role,
        changed: "changed" in role ? role.changed || false : false,
        roleOwner: (role.owner && role.owner.username) || "",
        deputyRoleOwner: (role.deputyOwner && role.deputyOwner.username) || "",
    };
};

export const fetchRequests = createAsyncThunk<
    { rows: ChangeRequest[]; total: number },
    { params: FetchTableDataParams },
    { rejectValue: ResponseError }
>("requests/fetchRequests", async ({ params }, { rejectWithValue }) => {
    try {
        const response = await requestsService.getRequests(params);
        return response;
    } catch (error) {
        return rejectWithValue(error as ResponseError);
    }
});

export const fetchEmployeeRequests = createAsyncThunk<
    { rows: ChangeRequest[]; total: number } | ResponseError,
    { params: FetchTableDataParams },
    { rejectValue: ResponseError }
>("requests/fetchEmployeeRequests", async ({ params }, { rejectWithValue }) => {
    try {
        const response = await requestsService.getEmployeeRequests(params);
        return response;
    } catch (error) {
        return rejectWithValue(error as ResponseError);
    }
});

export const fetchRequestsColumns = createAsyncThunk<Column[]>("requests/fetchRequestsColumns", async () => {
    const response = await requestsService.getRequestsColumns();
    return response;
});

export const createNewRequest = createAsyncThunk<GetRequestResponse, CreateRequestForm, { rejectValue: ResponseError }>(
    "requests/createNewRequest",
    async (requestData, { rejectWithValue }) => {
        try {
            const response = await requestsService.createNewRequest(requestData);
            return response.data;
        } catch (error) {
            return rejectWithValue(error as ResponseError);
        }
    }
);

export const fetchRequestStatuses = createAsyncThunk<GetRequestStatusesResponse>("requests/fetchRequestStatusesResponse", async () => {
    const response = await requestsService.getRequestsStatuses();
    return response;
});

export const lockRequest = createAsyncThunk<{ result: boolean; messages: ResponseError }, string, { rejectValue: ResponseError }>(
    "requests/lockRequest",
    async (requestId, { rejectWithValue }) => {
        try {
            const response = await lockManager.lockObject(requestId);
            return { result: true, messages: response as unknown as ResponseError };
        } catch (err) {
            return rejectWithValue(err as ResponseError);
        }
    }
);

export const fetchRequestDetailed = createAsyncThunk<
    RequestDetailed | ResponseError,
    { requestId: number | string; lockRequest?: boolean },
    { rejectValue: ResponseError }
>("requests/fetchRequestDetailed", async ({ requestId }, { rejectWithValue }) => {
    try {
        const response = await requestsService.getRequestDetailed(requestId);
        return response;
    } catch (error) {
        return rejectWithValue(error as ResponseError);
    }
});

export const clearCurrentRequest = createAsyncThunk<void, void, { state: RootState; rejectValue: null | ResponseError }>(
    "requests/clearCurrentRequest",
    async (__, { getState, rejectWithValue }) => {
        const state = getState();
        const currentRequest = state.administration.requests.currentRequest;
        const currentRequestEditable = state.administration.requests.currentRequest?.currentRequestEditable;
        if (!currentRequest) return rejectWithValue(null);

        if (!currentRequestEditable || !currentRequest.requestId) return;

        const lockResponse = await lockManager.unlockObject(`${currentRequest.requestId}`);

        if (!lockResponse.result) {
            return rejectWithValue(lockResponse.messages as ResponseError);
        }
    }
);
export const setModelingSelectedRole = createAsyncThunk<RequestModelingRole, RequestRole, { state: RootState }>(
    "requests/setModelingSelectedRole",
    async (role, { getState }) => {
        const state = getState();
        const modelingRole = selectModelingRoleById(state, role.id);
        if (modelingRole?.initialized) {
            return modelingRole;
        }

        const requestId = state.administration.requests.currentRequest?.requestId;
        if (!requestId) throw new Error("RequestId is not defined");

        const systemId = await requestsService.getTargetSystemId(modelingRole?.groupId || "");

        const {
            menu,
            tree,
            orgLevels,
            childrens = [],
            profile,
        } = await requestsService.getDetaliedRequestRole(requestId, role?.groupId, role?.role, role?.added);
        if (tree && orgLevels) {
            recalcRoleTreeTrafficLightStatus(tree, orgLevels);
        }

        const modelingRoleNew = createRoleForModeling(role, role.groupId);
        modelingRoleNew.systemId = typeof systemId === "string" ? systemId : String(systemId ?? "");
        modelingRoleNew.profile = profile;
        modelingRoleNew.menu = menu;
        modelingRoleNew.tree = tree ?? [];
        modelingRoleNew.orgLevels = orgLevels ?? {};
        modelingRoleNew.children = childrens;
        modelingRoleNew.initialized = true;

        return modelingRoleNew;
    }
);

export const moveToRejectStep = createAsyncThunk<void | ResponseError, { requestId: string; actionComment: string }, { rejectValue: ResponseError }>(
    "request/moveToRejectStep",
    async ({ requestId, actionComment }, { rejectWithValue }) => {
        try {
            const response = await requestsService.moveToRejectStep(requestId, actionComment);
            return response;
        } catch (error) {
            return rejectWithValue(error as ResponseError);
        }
    }
);

export const moveToApproveStep = createAsyncThunk<
    void | ResponseError,
    { requestId: string; actionComment: string; riskCheck: boolean },
    { rejectValue: ResponseError }
>("request/moveToApproveStep", async ({ requestId, actionComment, riskCheck = true }, { rejectWithValue }) => {
    try {
        const response = await requestsService.moveToApproveStep(requestId, actionComment, riskCheck);
        return response;
    } catch (error) {
        return rejectWithValue(error as ResponseError);
    }
});

export const setRequestStage = createAsyncThunk<void, { requestId: number; stage: string }, { rejectValue: ResponseError }>(
    "request/setRequestStage",
    async ({ requestId, stage }, { rejectWithValue }) => {
        try {
            const response = await requestsService.setRequestStage(requestId, stage);
            return response;
        } catch (error) {
            return rejectWithValue(error as ResponseError);
        }
    }
);

export const addRoleMenuNodeWithDefaultValues =
    ({ id, activeNodePath, tcodes }) =>
    async (dispatch, getState) => {
        let state = getState();
        let selectedRole = selectModelingRoleById(state, id);

        if (!selectedRole) {
            return;
        }

        const transactions = await getTransactionsByIdsForSystemsGroup(selectedRole.groupId, tcodes);

        dispatch(
            addRoleMenuNode({
                id,
                activeNodePath,
                tcodes,
                transactions,
            })
        );

        state = getState();
        selectedRole = selectModelingRoleById(state, id);
        const tcodesMapMenu = getTcodesMapCountFromMenu(selectedRole?.menu);

        const tcodesForDefaultPermissions = tcodes.filter((tcode) => tcodesMapMenu[tcode] && tcodesMapMenu[tcode] > 0);

        if (tcodesForDefaultPermissions.length > 0) {
            dispatch(
                addDefaultPermissionsForMenuUpdate({
                    id,
                    transactionIds: tcodesForDefaultPermissions,
                })
            );
        }

        // if (tcodesForDefaultPermissions.length > 0) {
        //     dispatch(
        //         addDefaultPermissionsForMenuUpdate({
        //             id,
        //             transactionIds: tcodesForDefaultPermissions,
        //         })
        //     );
        // }
    };
export const addDefaultPermissionsForMenuUpdate = createAsyncThunk<void, { id: string; transactionIds: string[] }, { state: RootState }>(
    "requests/addDefaultPermissionsForMenuUpdate",
    async ({ id, transactionIds }, { getState, dispatch }) => {
        const state = getState();
        const role = selectModelingRoleById(state, id);

        if (!role || !transactionIds?.length) {
            return;
        }

        const groupId = state.administration.requests.currentRequest?.groupId;
        if (!groupId) throw new Error("GroupId is not defined");

        const newPermissionsTree = await requestsService.getPfcgTreeForTcodes(groupId, transactionIds);

        const defaultValues = await getDefaultPermissionsValuesForTransactions(role.systemId, transactionIds, null);

        dispatch(
            addModelingRoleTreeItems({
                id,
                items: newPermissionsTree,
                defaultValues,
                tcodes: transactionIds,
                isStandard: true,
            })
        );
    }
);

export const removeGroupRoleChildFromModeling = createAsyncThunk<void, { roleIdToRemove: string }, { state: RootState }>(
    "requests/removeGroupRoleChildFromModeling",
    async ({ roleIdToRemove }, { getState, dispatch }) => {
        const state = getState();
        const selectedRoleId = state.administration.requests.modeling.selectedRoleId;
        const groupRole = state.administration.requests.modeling.roles.entities[selectedRoleId];
        const updatedChildren = groupRole?.children.filter((child) => child.role !== roleIdToRemove);

        dispatch(
            updateModelingRole({
                id: selectedRoleId,
                changes: { children: updatedChildren, changed: true },
            })
        );

        // const updatedRoles = roles
        // 	.map((role) => {
        // 		if (role.isComplex) {
        // 			return {
        // 				...role,
        // 				changed: true,
        // 				children: removeGroupRoleChildren(roleIdToRemove, role.children),
        // 			};
        // 		}
        // 		return role;
        // 	})
        // 	.filter((role) => role.id !== roleIdToRemove);

        // dispatch(removeRoleFromModeling(roleIdToRemove));
    }
);

export const addGroupRoleChildToModeling = createAsyncThunk<
    void,
    { newChildRole: { description: string; isComplex: boolean; profile: string; role: string; systemId: string; groupId?: string } },
    { state: RootState }
>("requests/addGroupRoleChildToModeling", async ({ newChildRole }, { getState, dispatch }) => {
    const state = getState();
    const selectedRoleId = state.administration.requests.modeling.selectedRoleId;
    const groupRole = state.administration.requests.modeling.roles.entities[selectedRoleId];
    const childRoleId = nanoid();

    if (newChildRole.groupId !== groupRole?.groupId) {
        newChildRole.groupId = groupRole?.groupId;
    }

    const updatedChildren = addGroupRoleChildren(newChildRole, childRoleId, groupRole?.children).map((child) => ({ ...child, isChild: false }));

    dispatch(
        updateModelingRole({
            id: selectedRoleId,
            changes: { children: updatedChildren, changed: true },
        })
    );
});

export const removeMenuItemFromTree = createAsyncThunk<void, { menuItemPath: string[]; id: string }, { state: RootState }>(
    "requests/removeMenuItem",
    async ({ menuItemPath, id }, { getState, dispatch }) => {
        const state = getState();

        const selectedRole = selectModelingRoleById(state, id);

        if (!selectedRole) {
            return;
        }

        const removedMenuItem = findItemInTree(menuItemPath, selectedRole.menu);

        const tcodesMapItem = getTcodesMapCountFromMenu([removedMenuItem]);

        dispatch(
            removeRoleMenuNode({
                id: selectedRole.id,
                activeNodePath: menuItemPath,
            })
        );

        const tcodesMapMenu = getTcodesMapCountFromMenu(selectedRole.menu);

        const groupId = selectedRole.groupId;

        const tcodeToValue = await getDefaultPermissionsValuesForTransactionsMap(null, Object.keys(tcodesMapMenu), groupId);

        const { deletedTcodes, remainedTcodes, deletedAtBranchTcodes } = getDeletedTcodesFromMenu(tcodesMapItem, tcodesMapMenu);

        try {
            const { orgLevelsForDelete, profilesForDelete } = getDeletedDefaultValuesForTcodes(
                deletedTcodes,
                remainedTcodes,
                tcodeToValue,
                deletedAtBranchTcodes
            );

            dispatch(
                substractPfcgTreeFromRole({
                    id,
                    orgLevelsForDelete,
                    profilesForDelete,
                    removedTcodes: Array.from(deletedTcodes),
                })
            );
        } catch (error) {
            console.log(error);
        }
    }
);
export const copyRoleMenuWithDefaultValues =
    ({ id, menuToCopy }) =>
    (dispatch) => {
        if (!menuToCopy?.length) {
            return;
        }

        dispatch(
            copyRoleMenu({
                id,
                menuToCopy,
            })
        );

        const transactionIds = getTransactionFromRoleMenu(menuToCopy[0].children);

        dispatch(
            addDefaultPermissionsForMenuUpdate({
                id,
                transactionIds,
            })
        );
    };

export const findPermissionInTree = createAsyncThunk<void, { searchString: string }, { state: RootState }>(
    "requests/findPermissionInTree",
    async ({ searchString }, { getState, dispatch }) => {
        const state = getState();
        const roleId = state.administration.requests.modeling.selectedRoleId;
        const role = state.administration.requests.modeling.roles.entities[roleId];

        if (role && role.tree && role.tree.length) {
            const updatedTree = produce(role.tree, (tree) => {
                findFieldInTree(tree, searchString);
            });

            dispatch(
                updateModelingRole({
                    id: roleId,
                    changes: { tree: updatedTree },
                })
            );
        }
    }
);
export const saveRequest = createAsyncThunk<void, void, { state: RootState; rejectValue: ResponseError }>(
    "requests/saveRequest",
    async (_, { getState, dispatch, rejectWithValue }) => {
        const state = getState();
        const calcRoles = [...Object.values(state.administration.requests.modeling.roles.entities)];

        const currentRequest = state.administration.requests.currentRequest;

        if (currentRequest?.readMode || currentRequest?.currentStage !== requestStages.ROLE_DEFINITION) {
            return;
        }

        const requestRolesMap = currentRequest.roles.reduce((accumulator, role) => {
            accumulator[role.role] = role;

            return accumulator;
        }, {});

        const rolesData: PostRequestModelingRole[] = [];

        calcRoles.forEach((role) => {
            const parsedRole = reportsModelingService.parseRoleToTreeVariant(role);
            if (!role || !state.administration.requests.currentRequest || !state.administration.requests.currentRequest?.groupId) {
                throw new Error("Role or Request is not defined");
            }

            const requestRole = requestRolesMap[role?.role];

            const roleOwnerData = role?.roleOwner
                ? {
                      username: role.roleOwner ?? "",
                      email: "",
                  }
                : null;

            const roleDeputyData = role?.deputyRoleOwner
                ? {
                      username: role.deputyRoleOwner ?? "",
                      email: "",
                  }
                : null;

            rolesData.push({
                request_id: state.administration.requests.currentRequest?.requestId,
                role: role?.role,
                group_id: state.administration.requests.currentRequest?.groupId,
                description: role?.description || "",
                is_changed: !!role?.changed,
                is_complex: role?.isComplex,
                org_level_delta: parsedRole.org_levels,
                delta: parsedRole.delta,
                menu_tree: parsedRole.menu_tree,
                children: parsedRole.children,
                is_new: role?.isNew ? true : false,
                owner: roleOwnerData,
                deputy_owner: roleDeputyData,
                added: !!(requestRole && requestRole.added),
            });
        });

        const form = {
            request_id: state.administration.requests.currentRequest?.requestId,
            roles: rolesData,
        };

        if (!form.request_id) throw new Error("RequestId is not defined");

        try {
            await requestsService.saveRequestRoles(form.roles, form.request_id);
        } catch (error) {
            return rejectWithValue(error as ResponseError);
        }

        await dispatch(resetRoles());

        const currentRole = selectModelingSelectedRole(state);
        const currentRequestRole = currentRole && currentRequest?.roles.find((role) => role.role === currentRole.role);
        if (currentRequestRole) {
            await dispatch(setModelingSelectedRole(currentRequestRole));
        }
    }
);

export const saveRequestComment = createAsyncThunk<
    RequestComment | ResponseError,
    { comment: string },
    { state: RootState; rejectValue: ResponseError }
>("request/saveRequestComment", async ({ comment }, { getState, rejectWithValue }) => {
    const state = getState();
    const currentUser = state?.auth?.user as unknown as Partial<{ username: string; lastName: string; firstName: string }>;
    const requestId = state.administration.requests.currentRequest?.requestId;
    const currentDate = new Date();
    const requestCommentData = {
        author: currentUser?.username ?? "",
        comment: comment,
        created_at: currentDate,
    };
    // const response = await requestsService.saveRequestComment(requestCommentData, requestId )
    try {
        if (!requestId) {
            throw new Error("RequestId is not defined");
        }
        await requestsService.saveRequestComment(requestCommentData, requestId);
        return {
            author: currentUser?.username ?? "",
            authorText: `${currentUser?.lastName ?? ""} ${currentUser?.firstName ?? ""}`,
            comment,
            createdAt: currentDate,
        };
    } catch (error) {
        return rejectWithValue(error as ResponseError);
    }
});

export const saveReportColumns = createAsyncThunk<
    { columns: ReportColumn[]; reportLevel: string; reportType: string; conflictLevel: string },
    { columns: ReportColumn[]; reportLevel: string; reportType: string; conflictLevel: string }
>("requests/saveReportColumns", async ({ columns, reportLevel, reportType, conflictLevel }) => {
    if (conflictLevel === REPORT_MODELING_CONFLICT_LEVELS.USERNAME) {
        await reportsModelingService.saveUserLevelModelingColumns(columns, reportLevel, reportType);
    } else {
        await reportsModelingService.saveRoleLevelModelingColumns(columns, reportLevel, reportType);
    }
    return { columns, reportLevel, reportType, conflictLevel };
});

export const saveTestingData = createAsyncThunk<
    void | ResponseError,
    {
        form: PreparationForTestingRequestFormState;
        requestId: string;
        riskCheck: boolean;
        approveWithConflictsMessage?: string;
    },
    {
        state: RootState;
        rejectValue: ResponseError;
    }
>("requests/saveTestingData", async ({ form, requestId, riskCheck, approveWithConflictsMessage = "" }, { rejectWithValue }) => {
    try {
        return await requestsService.saveTestingData(form, requestId, riskCheck, approveWithConflictsMessage);
    } catch (error) {
        return rejectWithValue(error as ResponseError);
    }
});

export const getRequestStages = createAsyncThunk<
    { stage: string; text: string }[] | ResponseError,
    void,
    { state: RootState; rejectValue: ResponseError }
>("requests/getRequestStage", async (_, { getState, rejectWithValue }) => {
    const state = getState();

    const currentStages = state.administration.requests.requestStages;

    if (currentStages.length) {
        return currentStages;
    }

    try {
        const response = await requestsService.getRequestStages();
        return response;
    } catch (error) {
        return rejectWithValue(error as ResponseError);
    }
});

const selectedRoleReducers = getSelectedRoleReducers(
    (state: stateType) => state.modeling.roles,
    modelingRoles,
    (state: stateType, role: RequestModelingRole) => {
        const requestRoles = state.rolesForView.roles;
        const changedRole = requestRoles.find((item) => item.id === role.id);
        if (changedRole) {
            changedRole.changed = true;
        }
        // requestRoles.forEach((r) => {
        //   if (r.isComplex) {
        //     r.children.forEach((child) => {
        //       if (child.id == role.id) {
        //         child.changed = true;
        //       }
        //     });
        //   }
        // });
        role.changed = true;
    },
    false
);

export const calcRequestReport = createAsyncThunk<void, string, { rejectValue: ResponseError }>(
    "requests/calcRequestReport",
    async (requestId, { rejectWithValue }) => {
        try {
            const response = await requestsService.calcRequestReport(requestId);
            return response;
        } catch (error) {
            return rejectWithValue(error as ResponseError);
        }
    }
);

export const fetchRequestReportResult = createAsyncThunk<
    { rows: []; total: number },
    { params: FetchTableDataParams },
    { state: RootState; rejectValue: ResponseError }
>("requests/fetchRequestReportResult", async ({ params }, { getState, rejectWithValue }) => {
    const state = getState();

    // if (!state.reportsModeling.roleLevel.canFetchResults) {
    //     return;
    // }

    const conflictLevel = state.administration.requests.reportState.conflictLevel;
    const requestId = state.administration.requests.currentRequest?.requestId;

    const newParams = {
        ...params,
        report_level: state.administration.requests.reportState.reportLevel,
        report_type: state.administration.requests.reportState.reportType,
        conflict_level: conflictLevel,
    };

    // const response = conflictLevel === REPORT_MODELING_CONFLICT_LEVELS.USERNAME
    //     ? await reportsModelingService.getUserLevelReportResults(newParams)
    //     : await reportsModelingService.getRoleLevelReportResults(newParams);

    try {
        if (!requestId) throw new Error("RequestId is not defined");
        const response = await requestsService.fetchRequestReportResult(requestId, newParams);
        return response;
    } catch (error) {
        return rejectWithValue(error as ResponseError);
    }
});
export const fetchRequestReportResultColumns = createAsyncThunk<Column[], void, { state: RootState }>(
    "requests/fetchRequestReportResultColumns",
    async (_, { getState }) => {
        const state = getState();
        const conflictLevel = state.administration.requests.reportState.conflictLevel;

        const columns =
            conflictLevel === REPORT_MODELING_CONFLICT_LEVELS.USERNAME
                ? await reportsModelingService.getUserLevelModelingColumns(
                      state.administration.requests.reportState.reportLevel,
                      state.administration.requests.reportState.reportType
                  )
                : await reportsModelingService.getRoleLevelModelingColumns(
                      state.administration.requests.reportState.reportLevel,
                      state.administration.requests.reportState.reportType
                  );
        // const columns =
        return columns;
    }
);

export const setReportLevelWithColumns = (reportLevel) => (dispatch) => {
    dispatch(setReportLevel(reportLevel));
    dispatch(fetchRequestReportResultColumns());
};

export const setReportTypeWithColumns = (reportType) => (dispatch) => {
    dispatch(setReportType(reportType));
    dispatch(fetchRequestReportResultColumns());
};

export const setConflictLevelWithColumns = (conflictLevel) => (dispatch) => {
    dispatch(setReportConflictLevel(conflictLevel));
    dispatch(fetchRequestReportResultColumns());
};

const requestsSlice = createSlice({
    name: "requests",
    initialState,
    reducers: {
        resetRoles: (state) => {
            const roles = Object.values(state.modeling.roles.entities).filter((r): r is RequestModelingRole => !!r);

            roles.forEach((role) => {
                if (role) {
                    role.initialized = false;
                    role.changed = false;
                    role.orgLevelsDelta = [];
                    role.delta = [];
                }
            });

            modelingRoles.setAll(state.modeling.roles, roles);
        },
        updateModelingRole: (state, action: PayloadAction<{ id: string; changes: Partial<RequestModelingRole> }>) => {
            modelingRoles.updateOne(state.modeling.roles, {
                id: action.payload.id,
                changes: action.payload.changes,
            });
        },
        removeSelectedRolesFromRequest: (state: stateType) => {
            const selectedRoleNames = state.rolesForView.selectedRoles;

            for (const role of Object.values(state.modeling.roles.entities)) {
                if (!role || !Array.isArray(role.children)) continue;

                const newChildren = role.children.filter((child) => {
                    if (!child?.role) return true;

                    if (!selectedRoleNames.includes(child.role)) return true;

                    const requestRole = state.rolesForView.roles.find((r) => r.role === child.role);
                    return !requestRole?.isNew;
                });

                if (newChildren.length !== role.children.length) {
                    modelingRoles.updateOne(state.modeling.roles, {
                        id: role.id,
                        changes: { children: newChildren, changed: true },
                    });
                }
            }

            state.rolesForView.roles = state.rolesForView.roles.filter((role) => !selectedRoleNames.includes(role.role));

            const modelingRoleIdsToRemove = Object.values(state.modeling.roles.entities)
                .filter((r) => r && selectedRoleNames.includes(r.role))
                .map((r) => r!.id);

            modelingRoles.removeMany(state.modeling.roles, modelingRoleIdsToRemove);

            const remainingRoleNames = new Set(state.rolesForView.roles.map((r) => r.role));
            if (state.currentRequest) {
                state.currentRequest.roles = state.currentRequest.roles.filter((r) => remainingRoleNames.has(r.role));

                if (!state.rolesForView.roles.length) {
                    state.currentRequest.groupId = "";
                }
            }

            if (!state.rolesForView.roles.length && state.currentRequest) {
                state.currentRequest.groupId = "";
            }

            state.rolesForView.selectedRoles = [];
        },
        setValidationErrors(state, action) {
            state.validationErrors = action.payload;
        },

        setSearchString(state, action: PayloadAction<string>) {
            if (state.searchString !== action.payload) {
                state.pageNumber = 1;
                state.searchString = action.payload;
            }
        },
        setSelectedIds(state: stateType, action: PayloadAction<string[]>) {
            state.selectedIds = action.payload;
        },
        setSelectedRoles(state: stateType, action: PayloadAction<string[]>) {
            state.rolesForView.selectedRoles = action.payload;
        },
        setPageNumber(state: stateType, action: PayloadAction<number>) {
            state.pageNumber = action.payload;
        },
        setLimitNumber(state: stateType, action: PayloadAction<number>) {
            state.pageNumber = 1;
            state.limitNumber = action.payload;
        },
        setSortData(state: stateType, action: PayloadAction<TableSortData[]>) {
            state.pageNumber = 1;
            state.sortData = action.payload;
        },
        setFilterData(state: stateType, action: PayloadAction<TableFilterData[]>) {
            state.pageNumber = 1;
            state.filterData = action.payload;
        },
        setError(state: stateType, action: PayloadAction<ResponseError | null>) {
            state.error = action.payload;
        },
        setWarning(state: stateType, action) {
            state.warning = action.payload;
        },
        setRequestsType(state: stateType, action: PayloadAction<string>) {
            state.pageNumber = 1;
            state.searchString = "";
            state.filterData = [];
            state.sortData = [];
            state.requestsType = action.payload;
        },

        addRolesToRequest(state: stateType, action: PayloadAction<{ roles: RequestRole[]; groupId: string }>) {
            const { roles, groupId } = action.payload;
            const parsedModelingRoles: Record<string, RequestModelingRole> = {};

            // const existingRoleNames = new Set(state.rolesForView.roles.map((role) => role.role));
            // const duplicateRoles = roles.filter((role) => existingRoleNames.has(role.role));

            // if (duplicateRoles.length > 0) {
            //     const duplicateRoleNames = duplicateRoles.map((role) => role.role).join(", ");
            //     state.error = {
            //         code: "VALIDATION_ERROR",
            //         type: "error",
            //         title: "Ошибка валидации",
            //         detail: `Роли с такими именами уже существуют: ${duplicateRoleNames}`,
            //         errors: [],
            //     };
            //     return;
            // }

            (state.currentRequest?.roles || []).forEach((role) => {
                if (role.groupId !== groupId) {
                    throw Error();
                }
            });

            roles.forEach((role) => {
                const isNew = !!role?.isNew;
                const roleExtended = createRoleForModeling(role, groupId, !isNew);

                parsedModelingRoles[roleExtended.id] = {
                    ...roleExtended,
                    isNew,
                    ...(isNew && { initialized: true }),
                };
            });

            modelingRoles.addMany(state.modeling.roles, parsedModelingRoles);
            state.rolesForView.roles = [...state.rolesForView.roles, ...roles];
            if (state.currentRequest) {
                state.currentRequest.groupId = groupId;
                state.currentRequest.roles = [...(state.currentRequest?.roles || []), ...roles];
            }
        },

        expandItemTree(state, action) {
            const roleId = state.modeling.selectedRoleId;
            const role = state.modeling.roles.entities[roleId];
            const item = findItemInTree(action.payload.path, role?.tree);
            item.open = !item.open;
            const treeWalker = new TreeWalker(item.children);
            treeWalker.walk(
                (node) => {
                    node.open = item.open;
                },
                () => {
                    return {
                        keyField: "",
                        keyValue: "",
                        childField: "children",
                    };
                },
                () => {}
            );
        },

        setReportLevel(state, action) {
            state.reportState.reportLevel = action.payload;
        },
        setReportType(state, action) {
            state.reportState.reportType = action.payload;
        },
        setReportConflictLevel(state, action) {
            state.reportState.conflictLevel = action.payload;
        },

        setReportPageNumber(state, action) {
            state.reportState.pageNumber = action.payload;
        },
        setReportLimitNumber(state, action) {
            state.reportState.limitNumber = action.payload;
            state.reportState.pageNumber = 1;
        },
        setReportSortData(state, action) {
            state.reportState.sortData = action.payload;
            state.reportState.pageNumber = 1;
        },
        setReportFilterData(state, action) {
            state.reportState.filterData = action.payload;
            state.reportState.pageNumber = 1;
        },
        setReportSearchString(state, action) {
            if (state.searchString !== action.payload) {
                state.reportState.pageNumber = 1;
                state.reportState.searchString = action.payload;
            }
        },
        setReportSelectedIds(state, action) {
            state.reportState.selectedIds = action.payload;
        },
        ...selectedRoleReducers,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchRequests.pending, (state) => {
                state.busy = true;
                state.busyType = BUSY_TYPES.LOAD;
            })
            .addCase(fetchRequests.rejected, (state, action) => {
                state.busy = false;
                state.error = action.payload || null;
            })
            .addCase(fetchRequests.fulfilled, (state, action) => {
                state.busy = false;
                state.requests = action.payload.rows;
                state.total = action.payload.total;
                state.error = null;
            })
            .addCase(fetchEmployeeRequests.fulfilled, (state, action) => {
                state.busy = false;
                if ("rows" in action.payload) {
                    state.requests = action.payload.rows;
                    state.total = action.payload.total;
                }
                state.error = null;
            })
            .addCase(fetchEmployeeRequests.pending, (state) => {
                state.busy = true;
                state.busyType = BUSY_TYPES.LOAD;
            })
            .addCase(fetchEmployeeRequests.rejected, (state, action) => {
                state.busy = false;
                state.error = action.payload || null;
            })
            .addCase(fetchRequestsColumns.pending, (state) => {
                state.busy = true;
                state.busyType = BUSY_TYPES.LOAD;
            })
            .addCase(fetchRequestsColumns.fulfilled, (state, action) => {
                state.busy = false;
                state.columns = action.payload;
            })
            .addCase(fetchRequestStatuses.fulfilled, (state, action) => {
                state.requestStatuses = action.payload;
            })
            .addCase(fetchRequestDetailed.fulfilled, (state, action) => {
                if ("roles" in action.payload) {
                    const roles = action.payload.roles;

                    state.currentRequest = action.payload;

                    const parsedModelingRoles = {};
                    roles.forEach((role: RequestRole) => {
                        role.id = nanoid();
                        const roleExtended = createRoleForModeling(role, role.groupId);

                        parsedModelingRoles[roleExtended.id] = roleExtended;
                        if (state.currentRequest) {
                            state.currentRequest.groupId = role.groupId;
                        }
                    });

                    modelingRoles.setAll(state.modeling.roles, parsedModelingRoles);

                    state.rolesForView.roles = roles;
                    state.busy = false;
                } else {
                    state.busy = false;
                    state.error = action.payload;
                }
            })

            .addCase(fetchRequestDetailed.rejected, (state, action) => {
                state.busy = false;
                state.error = action.payload || null;
            })
            .addCase(fetchRequestDetailed.pending, (state) => {
                state.busy = true;
                state.busyType = BUSY_TYPES.LOAD;
            })

            .addCase(lockRequest.fulfilled, (state, action) => {
                if (state.currentRequest) {
                    state.currentRequest.currentRequestEditable = action.payload?.result ?? true;
                }
                state.error = action.payload?.result ? null : action.payload?.messages;
            })
            .addCase(lockRequest.rejected, (state, action) => {
                if (state.currentRequest) {
                    state.currentRequest.currentRequestEditable = true;
                }
                state.error = action.payload || null;
            })

            .addCase(clearCurrentRequest.fulfilled, (state) => {
                state.busy = false;
                state.currentRequest = null;
                // state.currentRequestEditable = false;
                state.error = null;
                state.validationErrors = null;
            })
            .addCase(clearCurrentRequest.rejected, (state, action) => {
                state.busy = false;
                state.error = action.payload || null;
            })
            .addCase(clearCurrentRequest.pending, (state) => {
                state.busy = true;
                state.busyType = BUSY_TYPES.LOAD;
            })

            .addCase(getRequestStages.fulfilled, (state, action) => {
                if (Array.isArray(action.payload)) {
                    state.requestStages = action.payload;
                } else {
                    state.error = action.payload;
                }
            })

            .addCase(getRequestStages.rejected, (state, action) => {
                state.error = action.payload || null;
            })
            .addCase(setModelingSelectedRole.fulfilled, (state, action) => {
                const role = { ...action.payload };
                state.modeling.selectedRoleId = role.id;

                if (role.isComplex && !action.payload.initialized) {
                    role.children = role?.children.map((child) => ({ ...child, id: nanoid() }));
                }
                role.initialized = true;

                modelingRoles.setOne(state.modeling.roles, role);
                state.busy = false;
            })
            .addCase(setModelingSelectedRole.pending, (state) => {
                state.busy = true;
                state.busyType = BUSY_TYPES.LOAD;
            })
            .addCase(setModelingSelectedRole.rejected, (state) => {
                state.busy = false;
            })
            .addCase(saveRequest.rejected, (state, action) => {
                const error = action.payload;

                state.busy = false;

                if (error && error.code === "validation") {
                    const duplicates = error.errors?.filter((e) => e.code === "duplicate" && e.field === "roles").map((e) => e.value);

                    if (duplicates?.length) {
                        state.error = {
                            ...error,
                            detail: `${duplicates.join(", ")} уже находятся в другом запросе`,
                        };
                    }
                } else {
                    state.error = error ?? null;
                }
            })

            .addCase(saveRequest.pending, (state) => {
                state.busy = true;
                state.busyType = BUSY_TYPES.LOAD;
            })
            .addCase(saveRequest.fulfilled, (state) => {
                state.busy = false;
            })
            .addCase(createNewRequest.rejected, (state, action) => {
                state.busy = false;
                const error = action.payload;

                if (!error) return;

                const validationCodes = new Set(["no_integr_objects", "validation"]);

                if (error.code && validationCodes.has(error.code)) {
                    state.validationErrors = error.errors;
                }

                state.error = error;
            })
            .addCase(createNewRequest.fulfilled, (state) => {
                state.pageNumber =
                    state.total == 0
                        ? 1
                        : Number.isInteger(state.total / state.limitNumber)
                          ? Math.floor(state.total / state.limitNumber)
                          : Math.ceil(state.total / state.limitNumber);
                state.filterData = [];
                state.sortData = [];
                state.searchString = "";
            })
            .addCase(moveToRejectStep.fulfilled, (state) => {
                state.busy = false;
            })

            .addCase(moveToRejectStep.pending, (state) => {
                state.busy = true;
                state.busyType = BUSY_TYPES.LOAD;
            })

            .addCase(moveToRejectStep.rejected, (state, action) => {
                state.busy = false;
                state.error = action.payload || null;
            })
            .addCase(moveToApproveStep.fulfilled, (state, action) => {
                state.busy = false;
                if (action.payload) {
                    state.warning = action?.payload;
                }
            })
            .addCase(moveToApproveStep.rejected, (state, action) => {
                state.busy = false;
                state.error = action.payload || null;
            })
            .addCase(moveToApproveStep.pending, (state) => {
                state.busy = true;
                state.busyType = BUSY_TYPES.LOAD;
            })

            .addCase(setRequestStage.fulfilled, (state) => {
                state.busy = false;
            })
            .addCase(setRequestStage.rejected, (state, action) => {
                state.busy = false;
                state.error = action.payload || null;
            })
            .addCase(setRequestStage.pending, (state) => {
                state.busy = true;
                state.busyType = BUSY_TYPES.LOAD;
            })

            .addCase(saveTestingData.fulfilled, (state, action) => {
                state.busy = false;

                if (action.payload) {
                    state.warning = action?.payload;
                }
            })
            .addCase(saveTestingData.rejected, (state, action) => {
                state.busy = false;
                const error = action.payload;

                if (!error) return;

                const validationCodes = new Set(["no_integr_objects", "validation"]);

                if (error.code && validationCodes.has(error.code)) {
                    state.validationErrors = error.errors;
                }

                state.error = error;
            })
            .addCase(saveTestingData.pending, (state) => {
                state.busy = true;
                state.busyType = BUSY_TYPES.LOAD;
            })

            .addCase(calcRequestReport.fulfilled, (state) => {
                if (state.currentRequest) {
                    state.currentRequest.reportReady = true;
                }
                state.busy = false;
            })

            .addCase(calcRequestReport.pending, (state) => {
                state.busy = true;
                state.busyType = BUSY_TYPES.LOAD;
            })

            .addCase(calcRequestReport.rejected, (state, action) => {
                state.error = action.payload || null;
                if (state.currentRequest) {
                    state.currentRequest.reportReady = false;
                }
                state.busy = false;
            })

            .addCase(fetchRequestReportResult.fulfilled, (state, action) => {
                state.busy = false;
                state.reportState.total = action.payload.total;
                state.reportState.rows = action.payload.rows;
            })
            .addCase(fetchRequestReportResult.pending, (state) => {
                state.busy = true;
                state.busyType = BUSY_TYPES.LOAD;
            })
            .addCase(fetchRequestReportResult.rejected, (state, action) => {
                state.busy = false;
                state.error = action.payload || null;
            })

            .addCase(fetchRequestReportResultColumns.fulfilled, (state, action) => {
                state.reportState.columns = action.payload;
                state.reportState.pageNumber = 1;
                state.reportState.filterData = [];
                state.reportState.sortData = [];
            })

            .addCase(saveRequestComment.fulfilled, (state, action) => {
                if ("comment" in action.payload) {
                    state.currentRequest?.comments.push(action.payload);
                }
                state.busy = false;
            })
            .addCase(saveRequestComment.rejected, (state, action) => {
                state.busy = false;
                state.error = action.payload || null;
            })
            .addCase(saveRequestComment.pending, (state) => {
                state.busy = true;
                state.busyType = BUSY_TYPES.LOAD;
            })
            .addCase(saveReportColumns.fulfilled, (state, action) => {
                const { columns, reportLevel, reportType, conflictLevel } = action.payload;

                if (
                    state.reportState.reportLevel === reportLevel &&
                    state.reportState.reportType === reportType &&
                    state.reportState.conflictLevel === conflictLevel
                ) {
                    state.reportState.columns = columns.filter((column) => column.active);
                }
            });
    },
});
export const {
    setSearchString,
    setPageNumber,
    setLimitNumber,
    setSortData,
    setFilterData,
    setError,
    setRequestsType,
    setSelectedIds,
    setSelectedRoles,
    openRoleMenuNode,
    addRoleMenuFolder,
    addRoleMenuNode,
    addModelingRoleTreeItems,
    removeRoleMenuNode,
    substractPfcgTreeFromRole,
    setModelingRoleTreeField,
    setModelingRoleOrgLevelValues,
    copyRoleMenu,
    expandItemTree,
    removeModelingRoleTreeItem,
    updateModelingRole,
    removeSelectedRolesFromRequest,
    addRolesToRequest,
    setValidationErrors,
    resetRoles,
    setReportLevel,
    setReportPageNumber,
    setReportLimitNumber,
    setReportSearchString,
    setReportFilterData,
    setReportSortData,
    setReportType,
    setReportSelectedIds,
    setReportConflictLevel,
    setWarning,
} = requestsSlice.actions;

export default requestsSlice.reducer;

export const selectModelingSelectedRole = (state: RootState) => {
    const selectedRoleId = selectModelingSelectedRoleId(state);
    if (!selectedRoleId) return null;
    return selectModelingRoleById(state, selectedRoleId);
};

export const selectModelingSelectedRoleId = (state: RootState) => state.administration.requests.modeling.selectedRoleId;

export const selectChangeRequestGroupId = (state: RootState) => state.administration.requests.currentRequest?.groupId;
export const selectChangeRequestStage = (state: RootState) => state.administration.requests.currentRequest?.currentStage;

export const {
    selectIds: selectModelingRoleIds,
    selectAll: selectModelingRoles,
    selectById: selectModelingRoleById,
} = modelingRoles.getSelectors((state: RootState) => state.administration.requests.modeling.roles);
